import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { DialogActions } from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import {
  BUTTON_ADD,
  BUTTON_CREATE,
  BUTTON_GO_BACK,
  BUTTON_UPDATE,
  LOADING_INFORMATION,
  MODAL_CUSTOM_AUDIENCES_DESCRIPTION,
  MODAL_CUSTOM_AUDIENCES_TITLE,
  MODAL_EXCLUDED_CUSTOM_AUDIENCES_DESCRIPTION,
  MODAL_EXCLUDED_CUSTOM_AUDIENCES_TITLE,
} from "../../../../i18n/keysTranslations";
import { useDispatchApp } from "../../../../lib/redux";
import { useTranslationApp } from "../../../../lib/i18next";
import { startGetCustomAudiences } from "../../../../actions/adsCampaigns";
import { usePagination } from "../../../../hooks/usePagination";
import useNearScreen from "../../../../hooks/useNearScreen";
import Loader from "../../../Loaders/Loader";
import ModalBasicLayout from "../../../Modal/ModalBasicLayout";
import Button from "../../../Buttons/Button";
import { openLink } from "../../../../utils/string";
import ContainerModalActions from "../../../Containers/ContainerModalActions";
import CustomAudiencesList from "../../../Modal/ModalCustomAudiences/components/CustomAudiencesList";
import { TIKTOK } from "../../../../utils/constants";

const ModalCustomAudiencesListTikTok = ({
  modalOpen,
  integrationID,
  adAccountID,
  filteredCustomAudiences = [],
  onCloseModal = () => { },
  onCallbackSave = () => { },
  platform,
  isExcludedAudiences = false,
}) => {
  const [customAudiences, setCustomAudiences] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const onGetCustomAudiences = async () => {
    if (loadLastItem || isFetching) return;

    setIsFetching(true);

    const response = await dispatch(
      startGetCustomAudiences({
        platform,
        integrationID,
        adAccountID,
        lastKey,
        onChangeLastKey,
        onChangeLoadLastItem,
      })
    );

    setIsFetching(false);

    if (response.ok) {
      const data = response.data;

      setCustomAudiences((prev) =>
        [...prev, ...data].filter(
          (v) => !filteredCustomAudiences.includes(v.id)
        )
      );
    }

    setIsLoading(false);

    return response.ok;
  };

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: 10,
    onCallBackNextPage: onGetCustomAudiences,
  });

  const { fromRef, loadLastItem, onChangeLoadLastItem, isNearScreen } =
    useNearScreen({
      distance: "100px",
    });

  const onUpdateCustomAudiences = async () => {
    setIsLoading(true);

    const response = await dispatch(
      startGetCustomAudiences({
        platform,
        integrationID,
        adAccountID,
        onChangeLastKey,
        onChangeLoadLastItem,
      })
    );

    if (response.ok) {
      const data = response.data;

      setCustomAudiences(() =>
        [...data].filter((v) => !filteredCustomAudiences.includes(v.id))
      );
    }

    setIsLoading(false);

    return response.ok;
  };

  useEffect(() => {
    onGetCustomAudiences();
    // eslint-disable-next-line
  }, [isNearScreen]);

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={async () => {
        onCloseModal();
      }}
      maxWidth="sm"
      scroll="paper"
      title={t(
        isExcludedAudiences
          ? MODAL_EXCLUDED_CUSTOM_AUDIENCES_TITLE
          : MODAL_CUSTOM_AUDIENCES_TITLE
      )}
      description={t(
        isExcludedAudiences
          ? MODAL_EXCLUDED_CUSTOM_AUDIENCES_DESCRIPTION
          : MODAL_CUSTOM_AUDIENCES_DESCRIPTION
      )}
    >
      {isLoading && (
        <Loader hasMessage={true} message={t(LOADING_INFORMATION)} />
      )}

      {!isLoading && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 1,
              justifyContent: "flex-start",
              width: "100%",
              mb: 2,
              mt: 3.5,
            }}
          >
            <Button
              disabled={isLoading}
              color="secondary"
              startIcon={<CachedIcon />}
              onClick={onUpdateCustomAudiences}
              sx={{
                flex: 1,
                width: "100%",
              }}
            >
              {t(BUTTON_UPDATE)}
            </Button>
            <Button
              color="secondary"
              type="button"
              onClick={() =>
                openLink(
                  `https://ads.tiktok.com/i18n/dmp/adver/main?aadvid=${adAccountID}`
                )
              }
              sx={{
                flex: 1,
                width: "100%",
              }}
            >
              {t(BUTTON_CREATE)}
            </Button>
          </Box>
          <CustomAudiencesList
            selectedItems={selectedItems}
            onChangeSelectedItems={setSelectedItems}
            customAudiences={customAudiences}
            lastItemRef={fromRef}
            loadLastItem={loadLastItem}
            platform={TIKTOK}
          />
        </>
      )}
      <DialogActions
        sx={{
          p: 0,
        }}
      >
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_GO_BACK,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            disabled: selectedItems.length === 0,
            text: BUTTON_ADD,
            onClick: async () => {
              onCallbackSave(selectedItems);
            },
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalCustomAudiencesListTikTok;
